// lib/takeoff/itemSearch.js
// Trade-aware search over the item database (drywall, steel framing, ceilings, insulation, fasteners…).
//
// What it understands:
//   • sizes written any way:  5/8"  .625  0.625  ⅝   ·   3-5/8"  3 5/8  3.625  362  362S125-33
//   • gauge ⇄ mils:            20ga = 20 gauge = 33mil · 25ga = 18mil · 18ga = 43mil · 16ga = 54mil …
//   • sheet sizes / lengths:   4x8  4' x 8'  48x96 · 10'  10 ft  10-0
//   • trade words:             gwb = drywall = gyp = sheetrock = board · type x = fire rated · track = runner
//                              hat channel = furring · rc = resilient channel · crc = cold rolled · corner bead = cb …
//   • typos (one letter off), partial words while typing, item codes, labour vs material
// Results are ranked (best first); every word typed has to match (a near miss is only offered when nothing else does).
//
//   smartSearch(items, "5/8 x 4x10", { category: "M", limit: 150 })  → [item…]  (each with _score)

// ── gauge ⇄ mils (steel framing) ─────────────────────────────────────────────
const GA_MIL = { 25: [18, 19], 22: [27], 20: [33, 30], 18: [43], 16: [54], 14: [68], 12: [97] };
const MIL_GA = {};
for (const [ga, mils] of Object.entries(GA_MIL)) for (const m of mils) MIL_GA[m] = Number(ga);

// SSMA web/flange codes: 362 → 3-5/8", 162 → 1-5/8", 600 → 6" …
const SSMA_FRAC = { "00": 0, 12: 0.125, 25: 0.25, 37: 0.375, 50: 0.5, 62: 0.625, 75: 0.75, 87: 0.875 };
const ssmaInches = (code) => {
  const n = String(code);
  const whole = Number(n.slice(0, -2));
  const f = SSMA_FRAC[n.slice(-2)];
  return f == null ? null : whole + f;
};

// ── trade vocabulary: canonical token ← the ways people write it ─────────────
const SYN = {
  gwb: ["gwb", "drywall", "dry wall", "gypsum", "gyp", "gypbd", "gyp bd", "gyp board", "gypsum board", "sheetrock", "wallboard", "wall board", "board", "gyproc", "plasterboard"],
  typex: ["type x", "typex", "type-x", "fire rated", "fire-rated", "firecode", "fire code", "fireguard", "fire guard", "fire", "fr"],
  typec: ["type c", "typec", "type-c"],
  mr: ["moisture resistant", "moisture", "mold resistant", "mould resistant", "mold tough", "moldtough", "mold", "mould", "green board", "greenboard", "mr"],
  impact: ["abuse resistant", "abuse", "impact resistant", "high impact", "hi impact", "impact"],
  shaftliner: ["shaft liner", "shaftliner", "shaft wall", "shaftwall", "coreboard", "core board", "liner panel"],
  cementboard: ["cement board", "cementboard", "cement bd", "durock", "hardiebacker", "hardie backer", "tile backer", "tile board", "wonderboard"],
  sheathing: ["exterior sheathing", "sheathing", "densglass", "dens glass", "glass mat", "glassmat"],
  stud: ["stud", "studs", "c stud", "c-stud", "cee stud"],
  track: ["track", "tracks", "runner", "runners", "top track", "bottom track", "floor track", "u track", "u-track"],
  sliptrack: ["slip track", "sliptrack", "deflection track", "deep leg", "deep-leg", "deepleg", "slotted track", "dtrack", "head of wall"],
  furring: ["furring channel", "furring", "hat channel", "hat track", "hat", "z furring", "z-furring", "zee furring"],
  resilient: ["resilient channel", "resilient", "rc channel", "rc-1", "rc1", "rc"],
  crc: ["cold rolled channel", "cold rolled", "cold-rolled", "crc", "u channel", "u-channel", "carrying channel"],
  cornerbead: ["corner bead", "cornerbead", "outside corner", "cb"],
  jbead: ["j bead", "j-bead", "jbead", "j trim", "j-trim", "j mold", "j-mold", "j mould"],
  lbead: ["l bead", "l-bead", "lbead", "l trim", "l-trim", "tear away", "tear-away", "tearaway"],
  bullnose: ["bull nose", "bullnose", "bull-nose"],
  controljoint: ["control joint", "controljoint", "zip strip", "expansion joint", "cj"],
  screw: ["drywall screw", "framing screw", "screw", "screws", "type s", "type w", "fine thread", "coarse thread", "pan head", "wafer head", "self drilling", "self-drilling", "tek", "teks", "s12", "s-12", "fastener", "fasteners"],
  pin: ["powder actuated", "shot pin", "shot pins", "ramset", "hilti pin", "pin", "pins"],
  anchor: ["anchor", "anchors", "tapcon", "concrete screw", "wedge anchor"],
  compound: ["joint compound", "compound", "mud", "jc", "all purpose", "all-purpose", "lightweight", "easy sand", "easysand", "durabond", "setting compound", "hot mud", "topping"],
  tape: ["paper tape", "mesh tape", "joint tape", "fibatape", "tape", "taping"],
  finish: ["finish", "finishing", "level 4", "level 5", "lvl 4", "lvl 5", "l4", "l5"],
  insulation: ["sound attenuation", "acoustic batt", "acoustical batt", "sound batt", "mineral wool", "stone wool", "rockwool", "roxul", "safe n sound", "safe'n'sound", "safb", "batt", "batts", "insulation", "insul", "fiberglass", "fibreglass", "comfortboard"],
  sealant: ["acoustical sealant", "acoustic sealant", "sealant", "caulk", "caulking"],
  firestop: ["firestop", "fire stop", "fire caulk", "fire sealant", "intumescent"],
  act: ["acoustic ceiling tile", "acoustical ceiling tile", "acoustic ceiling", "acoustical ceiling", "ceiling tile", "ceiling tiles", "lay in", "lay-in", "act"],
  grid: ["t-bar", "t bar", "tbar", "main tee", "main runner", "cross tee", "wall angle", "wall mold", "wall moulding", "suspension", "grid"],
  wire: ["hanger wire", "hang wire", "tie wire", "wire"],
  frame: ["steel framing", "metal framing", "framing", "frame", "frm"],
  hang: ["board hanging", "hanging", "hang", "install", "installation", "inst"],
  steel: ["light gauge", "light gage", "cold formed", "steel", "metal", "galv", "galvanized", "lgs", "cfs"],
  wood: ["lumber", "wood", "timber", "spf"],
  accesspanel: ["access panel", "access door", "access hatch"],
  backing: ["backing", "blocking", "flat strap", "strap"],
  sheet: ["sheet", "sheets", "sht", "shts"],
  box: ["box", "boxes", "bx", "ctn", "carton"],
  piece: ["piece", "pieces", "pc", "pcs"],
  labour: ["labour", "labor", "lab", "manpower"],
  material: ["material", "materials", "mat", "matl"],
};
// single words → canonical; phrases (two+ words) → canonical, longest first
const WORD = new Map();
const PHRASES = [];
for (const [canon, list] of Object.entries(SYN))
  for (const v of list) {
    const k = v.toLowerCase();
    if (/[\s\-']/.test(k)) PHRASES.push([k, canon]);
    else if (!WORD.has(k)) WORD.set(k, canon);
  }
PHRASES.sort((a, b) => b[0].length - a[0].length);
const PHRASE_RE = PHRASES.map(([p, canon]) => [
  new RegExp(`(^|[^a-z0-9])${p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/[\s-]+/g, "[\\s-]*")}(?=$|[^a-z0-9])`, "g"),
  canon,
]);
const CANON = new Set(Object.keys(SYN));

const UNI_FRAC = { "¼": "1/4", "½": "1/2", "¾": "3/4", "⅛": "1/8", "⅜": "3/8", "⅝": "5/8", "⅞": "7/8", "⅓": "1/3", "⅔": "2/3" };
const cleanNum = (x) => {
  const v = Math.round(Number(x) * 10000) / 10000;
  return Number.isFinite(v) ? String(v) : String(x);
};

// text → normalised string + extra tokens found on the way (gauges, mils, SSMA sizes, sheet sizes)
function normalize(raw, forQuery = false) {
  let s = ` ${String(raw || "").toLowerCase()} `;
  const extra = [];
  const dims = []; // query only: each size written as AxB is ONE term (any of its spellings)
  s = s.replace(/(\d)?([¼½¾⅛⅜⅝⅞⅓⅔])/g, (_, d, f) => (d ? `${d}-${UNI_FRAC[f]}` : ` ${UNI_FRAC[f]}`));
  s = s.replace(/[“”″]|''/g, " in ").replace(/[‘’′]/g, "'");
  // SSMA member codes: 362S125-33, 600T125-54, 362s162-43 (50ksi)
  s = s.replace(/\b(\d{3})([stuf])(\d{3})(?:-(\d{2,3}))?\b/g, (m, web, kind, flange, mil) => {
    const w = ssmaInches(web);
    const f = ssmaInches(flange);
    if (w != null) extra.push(cleanNum(w), web);
    if (f != null) extra.push(cleanNum(f));
    extra.push(kind === "s" ? "stud" : kind === "t" ? "track" : kind === "u" ? "crc" : "furring");
    if (mil) {
      extra.push(`mil${mil}`);
      if (MIL_GA[mil]) extra.push(`ga${MIL_GA[mil]}`);
    }
    return ` ${m} `;
  });
  // 20ga · 20 ga · 20 gauge · 20g · 20 gage · 20eq / 20 ega
  s = s.replace(/(^|[^\d.])(\d{2})\s*(?:ga\b\.?|gauge|gage|g\b|ega\b|eq\b|ge\b)/g, (_, p, g) => {
    extra.push(`ga${g}`);
    for (const m of GA_MIL[g] || []) extra.push(`mil${m}`);
    return `${p} ga${g} `;
  });
  // 33mil · 33 mils · 43-mil
  s = s.replace(/(^|[^\d.])(\d{2,3})\s*-?\s*mils?\b/g, (_, p, m) => {
    extra.push(`mil${m}`);
    if (MIL_GA[m]) extra.push(`ga${MIL_GA[m]}`);
    return `${p} mil${m} `;
  });
  // feet / inches marks:  10'  10'-0"  5/8"
  s = s.replace(/(\d)\s*'\s*-?\s*(\d+)\s*(?:in|")/g, (_, a, b) => `${a} ft ${b} in `);
  s = s.replace(/(\d)\s*'/g, "$1 ft ").replace(/(\d)\s*"/g, "$1 in ");
  s = s.replace(/(\d)\s*(?:feet|foot|ft\.)\b/g, "$1 ft ").replace(/(\d)\s*(?:inches|inch|in\.)\b/g, "$1 in ");
  // mixed numbers  3-5/8  3 5/8  → 3.625 ;  fractions 5/8 → 0.625
  s = s.replace(/(\d+)(?:\s+|\s*-\s*)(\d+)\s*\/\s*(\d+)/g, (m, w, a, b) =>
    Number(b) ? ` ${cleanNum(Number(w) + Number(a) / Number(b))} ` : m,
  );
  s = s.replace(/(\d+)\s*\/\s*(\d+)/g, (m, a, b) => (Number(b) ? ` ${cleanNum(Number(a) / Number(b))} ` : m));
  // .625 → 0.625 ;  4.0 → 4
  s = s.replace(/(^|[^\d])\.(\d+)/g, "$10.$2");
  s = s.replace(/\d+\.\d+/g, (m) => cleanNum(m));
  // sheet sizes / dimension chains:  4 ft x 8 ft → 4x8 ; 0.625 in x 4 ft x 8 ft → 0.625x4x8, 4x8
  s = s.replace(/\d+(?:\.\d+)?(?:\s*(?:ft|in))?(?:\s*(?:x|×|by)\s*\d+(?:\.\d+)?(?:\s*(?:ft|in))?)+/g, (m) => {
    let nums = m.match(/\d+(?:\.\d+)?/g) || [];
    let lead = "";
    if (forQuery && nums.length > 2) {
      lead = nums[0]; // 5/8 x 4x8 → thickness, then the sheet size
      nums = nums.slice(1);
    }
    const toks = forQuery ? [] : [...nums];
    for (let i = 0; i + 1 < nums.length; i++) toks.push(`${nums[i]}x${nums[i + 1]}`);
    if (nums.length > 2) toks.push(nums.join("x"));
    // 48x96 (inches) ⇄ 4x8 (feet)
    if (nums.length >= 2) {
      const [a, b] = nums.slice(-2).map(Number);
      if (a % 12 === 0 && b % 12 === 0 && a >= 24) toks.push(`${a / 12}x${b / 12}`);
      if (a <= 6 && b <= 16 && Number.isInteger(a) && Number.isInteger(b)) toks.push(`${a * 12}x${b * 12}`);
    }
    if (forQuery) {
      dims.push([...new Set(toks)]);
      return ` ${lead} dimgrp${dims.length - 1} `;
    }
    return ` ${toks.join(" ")} `;
  });
  // stud web sizes written as 362 / 600 next to stud / track words
  s.replace(/\b(162|250|350|362|400|550|600|800|1000)\b/g, (m) => {
    const v = ssmaInches(m);
    if (v != null) extra.push(cleanNum(v));
    return m;
  });
  // 10 ft → also "10ft"
  s.replace(/(\d+(?:\.\d+)?)\s*ft\b/g, (_, n) => (extra.push(`${n}ft`), ""));
  return { s, extra, dims };
}

const stem = (t) => {
  if (/^\d/.test(t) || t.length <= 3) return t;
  if (t.endsWith("ies") && t.length > 4) return t.slice(0, -3) + "y";
  if (t.endsWith("ss")) return t;
  if (t.endsWith("s")) return t.slice(0, -1);
  return t;
};
const splitTokens = (s) => s.split(/[^a-z0-9.]+/).map((t) => t.replace(/^\.+|\.+$/g, "")).filter(Boolean);

// everything an item's text should be findable by
function analyzeText(raw) {
  const { s, extra } = normalize(raw);
  const out = new Set(extra);
  // phrases first ("fire caulk" is firestop, not Type X); their words keep only their own spelling
  let rest = s;
  for (const [re, canon] of PHRASE_RE) {
    re.lastIndex = 0;
    if (re.test(rest)) {
      out.add(canon);
      re.lastIndex = 0;
      rest = rest.replace(re, "$1 ");
    }
  }
  for (const t of splitTokens(s)) {
    out.add(t);
    out.add(stem(t));
  }
  for (const t of splitTokens(rest)) {
    const c = WORD.get(t) || WORD.get(stem(t));
    if (c) out.add(c);
  }
  return out;
}

// the query → required terms; each term is a list of alternatives (any one matches)
function analyzeQuery(q) {
  let { s, extra, dims } = normalize(q, true);
  const terms = [];
  // phrases become one term ("type x", "hat channel", "corner bead")
  for (const [re, canon] of PHRASE_RE) {
    re.lastIndex = 0;
    if (re.test(s)) {
      terms.push({ alts: [canon], canon: true, raw: canon });
      re.lastIndex = 0;
      s = s.replace(re, "$1 ");
    }
  }
  const toks = splitTokens(s);
  const extraSet = new Set(extra);
  toks.forEach((t, i) => {
    if (["in", "ft", "by", "and", "the", "of", "for", "with", "w", "-"].includes(t)) return;
    // a lone "x" (not between two numbers) is how estimators write Type X: "5/8 x"
    if (t === "x") return terms.push({ alts: ["typex", "x"], raw: "x", canon: true });
    const dg = t.match(/^dimgrp(\d+)$/);
    if (dg) return terms.push({ alts: dims[Number(dg[1])] || [], raw: t, canon: true, num: true });
    const st = stem(t);
    const c = WORD.get(t) || WORD.get(st);
    const alts = [...new Set([t, st, ...(c ? [c] : [])])];
    // ga20 ↔ mil33 are the same thing
    const g = t.match(/^ga(\d+)$/);
    if (g) for (const m of GA_MIL[g[1]] || []) alts.push(`mil${m}`);
    const m = t.match(/^mil(\d+)$/);
    if (m && MIL_GA[m[1]]) alts.push(`ga${MIL_GA[m[1]]}`);
    terms.push({ alts, raw: t, last: i === toks.length - 1, num: /^\d/.test(t) });
  });
  // extras the query produced that aren't already a term (e.g. "362S" → 3.625)
  for (const e of extraSet) if (!terms.some((t) => t.alts.includes(e)) && /^\d/.test(e)) terms.push({ alts: [e], raw: e, num: true, soft: true });
  return terms;
}

// ── index (built once per catalog array) ─────────────────────────────────────
const CACHE = new WeakMap();
function buildIndex(items) {
  let idx = CACHE.get(items);
  if (idx) return idx;
  const vocab = new Set();
  const entries = items.map((item) => {
    const code = analyzeText(`${item.itemCode || ""} ${item.partNumber || ""}`);
    const desc = analyzeText(`${item.description || item.title || ""}`);
    const size = analyzeText(`${item.size || ""}`);
    const other = analyzeText(
      `${item.sectionNumber || ""} ${item.costCode?.code || ""} ${item.costCode?.name || ""} ${item.manufacturer || ""} ${item.uom || ""} ${item.itemNotes || ""} ${item.category === "L" ? "labour" : "material"}`,
    );
    for (const set of [code, desc, size, other]) for (const t of set) vocab.add(t);
    return {
      item,
      code,
      desc,
      size,
      other,
      codeLower: String(item.itemCode || "").toLowerCase(),
      descNorm: normalize(item.description || item.title || "").s.replace(/\s+/g, " "),
    };
  });
  idx = { entries, vocab: [...vocab] };
  CACHE.set(items, idx);
  return idx;
}

function lev1(a, b) {
  // true if a and b are within one edit (insert / delete / substitute / swap)
  if (a === b) return true;
  const la = a.length,
    lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  while (i < la && i < lb && a[i] === b[i]) i++;
  if (la === lb) return a.slice(i + 1) === b.slice(i + 1) || (a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2));
  return la > lb ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/**
 * smartSearch(items, query, { category: "M"|"L"|"all", limit }) → ranked items (copies with _score, _partial)
 */
export function smartSearch(items = [], query = "", { category = "all", limit = 150 } = {}) {
  const pool = Array.isArray(items) ? items : [];
  const catOk = (i) => category === "all" || !category || String(i.category || "").toUpperCase() === category;
  const q = String(query || "").trim();
  if (!q) return pool.filter(catOk).slice(0, limit);
  const { entries, vocab } = buildIndex(pool);
  const terms = analyzeQuery(q);
  if (!terms.length) return pool.filter(catOk).slice(0, limit);

  // widen each term once against the whole vocabulary: word starts (while typing) and one-letter typos
  for (const t of terms) {
    t.weighted = t.alts.map((a, k) => ({ tok: a, w: k === 0 ? 10 : 9 }));
    if (t.canon) continue;
    const r = t.raw;
    if (!t.num && r.length >= 2) {
      let n = 0;
      for (const v of vocab)
        if (v !== r && v.startsWith(r)) {
          t.weighted.push({ tok: v, w: 6 });
          const c = WORD.get(v);
          if (c) t.weighted.push({ tok: c, w: 5 });
          if (++n > 60) break;
        }
    } else if (t.num && t.last) {
      // a number being typed: "3.6" → 3.625
      for (const v of vocab) if (v !== r && /^\d/.test(v) && v.startsWith(r)) t.weighted.push({ tok: v, w: 5 });
    }
    if (!t.num && r.length >= 4)
      for (const v of vocab)
        if (!/^\d/.test(v) && v.length >= 4 && lev1(r, v)) {
          t.weighted.push({ tok: v, w: 4 });
          const c = WORD.get(v);
          if (c) t.weighted.push({ tok: c, w: 4 });
        }
  }
  const qLower = q.toLowerCase();
  const qNorm = normalize(q).s.replace(/\s+/g, " ").trim();

  const scoreOf = (e) => {
    let score = 0,
      miss = 0;
    for (const t of terms) {
      let best = 0;
      for (const { tok, w } of t.weighted) {
        if (e.code.has(tok)) best = Math.max(best, w * 1.3);
        else if (e.desc.has(tok)) best = Math.max(best, w);
        else if (e.size.has(tok)) best = Math.max(best, w * 0.9);
        else if (e.other.has(tok)) best = Math.max(best, w * 0.6);
      }
      if (!best && !t.num && t.raw.length >= 2 && (e.codeLower.includes(t.raw) || e.descNorm.includes(t.raw))) best = 3;
      if (best) score += best;
      else if (!t.soft) miss++;
    }
    if (e.codeLower === qLower) score += 60;
    else if (e.codeLower.startsWith(qLower)) score += 25;
    if (qNorm.length > 2 && e.descNorm.includes(qNorm)) score += 12;
    return { score, miss };
  };

  const hard = terms.filter((t) => !t.soft).length;
  const strict = [];
  const loose = [];
  for (const e of entries) {
    if (!catOk(e.item)) continue;
    const { score, miss } = scoreOf(e);
    if (!miss) strict.push({ e, score });
    else if (miss === 1 && hard >= 2 && score > 8) loose.push({ e, score: score - 15 });
  }
  const sortFn = (a, b) => b.score - a.score || String(a.e.item.itemCode).localeCompare(String(b.e.item.itemCode));
  strict.sort(sortFn);
  let out = strict.map(({ e, score }) => ({ ...e.item, _score: score }));
  if (out.length < 5 && loose.length) {
    loose.sort(sortFn);
    out = out.concat(loose.slice(0, limit).map(({ e, score }) => ({ ...e.item, _score: score, _partial: true })));
  }
  return out.slice(0, limit);
}

// quick filters for the search box (each adds its words to the query)
export const SEARCH_CHIPS = [
  ["Board", "gwb"],
  ["Type X", "type x"],
  ["Studs", "stud"],
  ["Track", "track"],
  ["Slip track", "slip track"],
  ["Furring", "furring"],
  ["RC", "resilient"],
  ["Bead / trim", "bead"],
  ["Screws", "screw"],
  ["Compound", "compound"],
  ["Tape", "tape"],
  ["Insulation", "insulation"],
  ["Sealant", "sealant"],
  ["Ceiling", "act"],
  ["Grid", "grid"],
];

// for tests / debugging
export const _internals = { normalize, analyzeText, analyzeQuery };
